import { CYCLING_ANGLES } from './CyclingAngles';

/**
 * Generate concrete bike fit adjustment recommendations based on angle analysis results.
 * 
 * This module translates body angle measurements into actionable bike adjustments
 * (mm for position changes, degrees for tilt changes) based on scientific research.
 * 
 * See docs/bike-fit-research.md for scientific backing.
 */

/**
 * Priority order for adjustments (lower number = higher priority)
 */
const ADJUSTMENT_PRIORITY = {
  saddle_height: 1,
  saddle_fore_aft: 2,
  handlebar_height: 3,
  handlebar_reach: 4,
  saddle_tilt: 5,
  handlebar_tilt: 6,
};

/**
 * Confidence levels for recommendations
 */
const CONFIDENCE = {
  HIGH: 'high', // Direct, well-researched relationship
  MEDIUM: 'medium', // Research-backed but varies by individual
  LOW: 'low', // Approximation based on biomechanics
};

/**
 * Generate bike fit adjustment tips from cycling angle analysis results.
 * 
 * @param {Object} results - Output from CyclingAnalyzer.computeResults()
 * @returns {Object} - Structured adjustment recommendations
 */
export function generateBikeFitTips(results) {
  if (!results || !results.stable || !results.angles) {
    return {
      hasRecommendations: false,
      reason: 'Insufficient data for recommendations',
    };
  }

  const recommendations = [];

  // Process each angle and generate recommendations
  for (const angleDef of CYCLING_ANGLES) {
    const angleResult = results.angles[angleDef.id];
    if (!angleResult || !angleResult.valid) continue;

    const tips = generateTipsForAngle(angleDef, angleResult);
    recommendations.push(...tips);
  }

  // Remove duplicates and resolve conflicts
  const resolvedRecommendations = resolveConflicts(recommendations);

  // Group by adjustment type
  const grouped = groupRecommendations(resolvedRecommendations);

  // Add general tilt recommendations
  const tiltRecommendations = generateTiltRecommendations(results);
  if (tiltRecommendations.saddle) {
    grouped.saddle_tilt = [tiltRecommendations.saddle];
  }
  if (tiltRecommendations.handlebar) {
    grouped.handlebar_tilt = [tiltRecommendations.handlebar];
  }

  return {
    hasRecommendations: resolvedRecommendations.length > 0,
    recommendations: resolvedRecommendations,
    grouped,
    disclaimer: 'These are estimates based on scientific research. Start with smaller adjustments and test gradually. Professional bike fitting is recommended for precision.',
  };
}

/**
 * Generate specific tips for a single angle measurement
 */
function generateTipsForAngle(angleDef, angleResult) {
  const tips = [];
  const { evaluatedValue, status } = angleResult;

  // Skip if angle is optimal
  if (status.status === 'optimal') {
    return tips;
  }

  const deviation = evaluatedValue - (angleDef.optimalMin + angleDef.optimalMax) / 2;
  const targetMid = (angleDef.optimalMin + angleDef.optimalMax) / 2;

  switch (angleDef.id) {
    case 'knee':
      tips.push(...generateKneeTips(evaluatedValue, deviation, targetMid, angleDef));
      break;
    case 'hip':
      tips.push(...generateHipTips(evaluatedValue, deviation, targetMid, angleDef));
      break;
    case 'torso':
      tips.push(...generateTorsoTips(evaluatedValue, deviation, targetMid, angleDef));
      break;
    case 'elbow':
      tips.push(...generateElbowTips(evaluatedValue, deviation, targetMid, angleDef));
      break;
    case 'shoulder':
      tips.push(...generateShoulderTips(evaluatedValue, deviation, targetMid, angleDef));
      break;
    case 'ankle':
      // Ankle issues typically resolved by saddle height/cleat position
      // Already covered by knee recommendations
      if (angleResult.rangeWarning) {
        tips.push({
          type: 'saddle_height',
          category: 'Saddle',
          action: 'Check saddle height and cleat position',
          direction: null,
          estimateMm: null,
          reason: `Excessive ankle movement (${Math.round(angleResult.rangeWarning.range * 10) / 10}° range)`,
          detail: 'Cue "quiet ankles" during pedaling. Consider cleat adjustment.',
          priority: ADJUSTMENT_PRIORITY.saddle_height,
          confidence: CONFIDENCE.MEDIUM,
          sourceAngle: 'ankle',
        });
      }
      break;
    default:
      // No specific tips for unknown angles
      break;
  }

  return tips;
}

/**
 * Generate recommendations for knee angle (affects saddle height)
 */
function generateKneeTips(value, deviation, target, angleDef) {
  const tips = [];

  if (value > angleDef.optimalMax) {
    // Knee angle too high (too much flexion) -> saddle too low
    const degreesToFix = value - angleDef.optimalMax;
    const estimateMm = Math.round(degreesToFix * 1.0); // 1mm per degree (Holmes Method)

    tips.push({
      type: 'saddle_height',
      category: 'Saddle',
      action: 'Raise saddle',
      direction: 'up',
      estimateMm,
      unit: 'mm',
      reason: `Knee angle ${(Math.round(value * 10) / 10).toFixed(1)}° (target ${angleDef.optimalMin}–${angleDef.optimalMax}°)`,
      detail: 'Too much knee flexion at bottom of pedal stroke. Raising saddle will reduce knee flexion.',
      priority: ADJUSTMENT_PRIORITY.saddle_height,
      confidence: CONFIDENCE.HIGH,
      sourceAngle: 'knee',
      currentValue: value,
      targetRange: [angleDef.optimalMin, angleDef.optimalMax],
    });
  } else if (value < angleDef.optimalMin) {
    // Knee angle too low (too little flexion) -> saddle too high
    const degreesToFix = angleDef.optimalMin - value;
    const estimateMm = Math.round(degreesToFix * 1.0);

    tips.push({
      type: 'saddle_height',
      category: 'Saddle',
      action: 'Lower saddle',
      direction: 'down',
      estimateMm,
      unit: 'mm',
      reason: `Knee angle ${(Math.round(value * 10) / 10).toFixed(1)}° (target ${angleDef.optimalMin}–${angleDef.optimalMax}°)`,
      detail: 'Too little knee flexion at bottom of pedal stroke. Lowering saddle will increase knee flexion.',
      priority: ADJUSTMENT_PRIORITY.saddle_height,
      confidence: CONFIDENCE.HIGH,
      sourceAngle: 'knee',
      currentValue: value,
      targetRange: [angleDef.optimalMin, angleDef.optimalMax],
    });
  }

  return tips;
}

/**
 * Generate recommendations for hip angle (affects saddle fore/aft)
 */
function generateHipTips(value, deviation, target, angleDef) {
  const tips = [];

  if (value < angleDef.optimalMin) {
    // Hip too closed -> move saddle back and/or raise handlebar
    const degreesToFix = angleDef.optimalMin - value;
    const estimateMm = Math.round(degreesToFix * 2.5); // 2-3mm per degree

    tips.push({
      type: 'saddle_fore_aft',
      category: 'Saddle',
      action: 'Move saddle back',
      direction: 'back',
      estimateMm,
      unit: 'mm',
      reason: `Hip angle too closed at ${(Math.round(value * 10) / 10).toFixed(1)}° (target ${angleDef.optimalMin}–${angleDef.optimalMax}°)`,
      detail: 'Moving saddle back opens hip angle and reduces hip impingement risk.',
      priority: ADJUSTMENT_PRIORITY.saddle_fore_aft,
      confidence: CONFIDENCE.MEDIUM,
      sourceAngle: 'hip',
      currentValue: value,
      targetRange: [angleDef.optimalMin, angleDef.optimalMax],
    });

    // Also suggest handlebar height as secondary adjustment
    const handlebarMm = Math.round(degreesToFix * 3);
    tips.push({
      type: 'handlebar_height',
      category: 'Handlebar',
      action: 'Raise handlebar (alternative)',
      direction: 'up',
      estimateMm: handlebarMm,
      unit: 'mm',
      reason: `Alternative to address closed hip angle`,
      detail: 'Raising handlebars can also help open hip angle.',
      priority: ADJUSTMENT_PRIORITY.handlebar_height + 0.5, // Lower priority than primary
      confidence: CONFIDENCE.MEDIUM,
      sourceAngle: 'hip',
      isSecondary: true,
    });
  } else if (value > angleDef.optimalMax) {
    // Hip too open -> move saddle forward
    const degreesToFix = value - angleDef.optimalMax;
    const estimateMm = Math.round(degreesToFix * 2.5);

    tips.push({
      type: 'saddle_fore_aft',
      category: 'Saddle',
      action: 'Move saddle forward',
      direction: 'forward',
      estimateMm,
      unit: 'mm',
      reason: `Hip angle too open at ${(Math.round(value * 10) / 10).toFixed(1)}° (target ${angleDef.optimalMin}–${angleDef.optimalMax}°)`,
      detail: 'Saddle may be too far back. Moving forward closes hip angle.',
      priority: ADJUSTMENT_PRIORITY.saddle_fore_aft,
      confidence: CONFIDENCE.MEDIUM,
      sourceAngle: 'hip',
      currentValue: value,
      targetRange: [angleDef.optimalMin, angleDef.optimalMax],
    });
  }

  return tips;
}

/**
 * Generate recommendations for torso angle (affects handlebar height)
 */
function generateTorsoTips(value, deviation, target, angleDef) {
  const tips = [];

  if (value < angleDef.optimalMin) {
    // Torso too flat/aggressive -> raise handlebar
    const degreesToFix = angleDef.optimalMin - value;
    const estimateMm = Math.round(degreesToFix * 4); // 3-5mm per degree

    tips.push({
      type: 'handlebar_height',
      category: 'Handlebar',
      action: 'Raise handlebar',
      direction: 'up',
      estimateMm,
      unit: 'mm',
      reason: `Torso angle too flat at ${(Math.round(value * 10) / 10).toFixed(1)}° (target ${angleDef.optimalMin}–${angleDef.optimalMax}°)`,
      detail: 'Raising handlebars makes position more upright, reducing strain on lower back and neck.',
      priority: ADJUSTMENT_PRIORITY.handlebar_height,
      confidence: CONFIDENCE.MEDIUM,
      sourceAngle: 'torso',
      currentValue: value,
      targetRange: [angleDef.optimalMin, angleDef.optimalMax],
    });
  } else if (value > angleDef.optimalMax) {
    // Torso too upright -> lower handlebar or increase reach
    const degreesToFix = value - angleDef.optimalMax;
    const estimateMm = Math.round(degreesToFix * 4);

    tips.push({
      type: 'handlebar_height',
      category: 'Handlebar',
      action: 'Lower handlebar',
      direction: 'down',
      estimateMm,
      unit: 'mm',
      reason: `Torso angle too upright at ${(Math.round(value * 10) / 10).toFixed(1)}° (target ${angleDef.optimalMin}–${angleDef.optimalMax}°)`,
      detail: 'Lowering handlebars creates more aerodynamic position. Requires good flexibility.',
      priority: ADJUSTMENT_PRIORITY.handlebar_height,
      confidence: CONFIDENCE.MEDIUM,
      sourceAngle: 'torso',
      currentValue: value,
      targetRange: [angleDef.optimalMin, angleDef.optimalMax],
    });
  }

  return tips;
}

/**
 * Generate recommendations for elbow angle (affects handlebar reach)
 */
function generateElbowTips(value, deviation, target, angleDef) {
  const tips = [];

  if (value < angleDef.optimalMin) {
    // Elbows too bent -> increase reach (longer stem)
    const degreesToFix = angleDef.optimalMin - value;
    const estimateMm = Math.round(degreesToFix * 2); // 2mm per degree

    tips.push({
      type: 'handlebar_reach',
      category: 'Handlebar',
      action: 'Increase reach (longer stem)',
      direction: 'forward',
      estimateMm,
      unit: 'mm',
      reason: `Elbow angle too bent at ${(Math.round(value * 10) / 10).toFixed(1)}° (target ${angleDef.optimalMin}–${angleDef.optimalMax}°)`,
      detail: 'Elbows too bent puts excess weight on hands. Longer stem straightens arms.',
      priority: ADJUSTMENT_PRIORITY.handlebar_reach,
      confidence: CONFIDENCE.MEDIUM,
      sourceAngle: 'elbow',
      currentValue: value,
      targetRange: [angleDef.optimalMin, angleDef.optimalMax],
    });
  } else if (value > angleDef.optimalMax) {
    // Elbows too straight/locked -> decrease reach (shorter stem)
    const degreesToFix = value - angleDef.optimalMax;
    const estimateMm = Math.round(degreesToFix * 2);

    tips.push({
      type: 'handlebar_reach',
      category: 'Handlebar',
      action: 'Decrease reach (shorter stem)',
      direction: 'back',
      estimateMm,
      unit: 'mm',
      reason: `Elbow angle too straight at ${(Math.round(value * 10) / 10).toFixed(1)}° (target ${angleDef.optimalMin}–${angleDef.optimalMax}°)`,
      detail: 'Locked elbows transmit shock to shoulders. Shorter stem allows slight elbow bend.',
      priority: ADJUSTMENT_PRIORITY.handlebar_reach,
      confidence: CONFIDENCE.MEDIUM,
      sourceAngle: 'elbow',
      currentValue: value,
      targetRange: [angleDef.optimalMin, angleDef.optimalMax],
    });
  }

  return tips;
}

/**
 * Generate recommendations for shoulder angle (affects handlebar reach)
 */
function generateShoulderTips(value, deviation, target, angleDef) {
  const tips = [];

  if (value < angleDef.optimalMin) {
    // Arms too close to body -> decrease reach (shorter stem)
    const degreesToFix = angleDef.optimalMin - value;
    const estimateMm = Math.round(degreesToFix * 2);

    tips.push({
      type: 'handlebar_reach',
      category: 'Handlebar',
      action: 'Decrease reach (shorter stem)',
      direction: 'back',
      estimateMm,
      unit: 'mm',
      reason: `Shoulder angle too closed at ${(Math.round(value * 10) / 10).toFixed(1)}° (target ${angleDef.optimalMin}–${angleDef.optimalMax}°)`,
      detail: 'Arms cramped close to body. Shorter stem opens shoulder angle.',
      priority: ADJUSTMENT_PRIORITY.handlebar_reach,
      confidence: CONFIDENCE.MEDIUM,
      sourceAngle: 'shoulder',
      currentValue: value,
      targetRange: [angleDef.optimalMin, angleDef.optimalMax],
    });
  } else if (value > angleDef.optimalMax) {
    // Reaching too far -> decrease reach (shorter stem)
    const degreesToFix = value - angleDef.optimalMax;
    const estimateMm = Math.round(degreesToFix * 2);

    tips.push({
      type: 'handlebar_reach',
      category: 'Handlebar',
      action: 'Decrease reach (shorter stem)',
      direction: 'back',
      estimateMm,
      unit: 'mm',
      reason: `Shoulder angle too open at ${(Math.round(value * 10) / 10).toFixed(1)}° (target ${angleDef.optimalMin}–${angleDef.optimalMax}°)`,
      detail: 'Reaching too far forward. Shorter stem reduces neck and upper back strain.',
      priority: ADJUSTMENT_PRIORITY.handlebar_reach,
      confidence: CONFIDENCE.MEDIUM,
      sourceAngle: 'shoulder',
      currentValue: value,
      targetRange: [angleDef.optimalMin, angleDef.optimalMax],
    });
  }

  return tips;
}

/**
 * Generate tilt recommendations based on overall posture
 */
function generateTiltRecommendations(results) {
  const tiltRecs = {};

  // Saddle tilt: generally recommend level to slight nose-down
  tiltRecs.saddle = {
    type: 'saddle_tilt',
    category: 'Saddle',
    action: 'Current position looks good',
    detail: 'Saddle should be level to 3° nose-down for comfort. Adjust if experiencing perineal discomfort.',
    priority: ADJUSTMENT_PRIORITY.saddle_tilt,
    confidence: CONFIDENCE.LOW,
    isGeneral: true,
  };

  // Handlebar tilt: standard recommendation
  tiltRecs.handlebar = {
    type: 'handlebar_tilt',
    category: 'Handlebar',
    action: 'Check wrist comfort',
    detail: 'Brake hoods should be slightly above horizontal. Adjust for neutral wrist angle.',
    priority: ADJUSTMENT_PRIORITY.handlebar_tilt,
    confidence: CONFIDENCE.LOW,
    isGeneral: true,
  };

  return tiltRecs;
}

/**
 * Resolve conflicts when multiple recommendations affect the same adjustment
 */
function resolveConflicts(recommendations) {
  const byType = {};

  // Group by type
  recommendations.forEach(rec => {
    if (!byType[rec.type]) {
      byType[rec.type] = [];
    }
    byType[rec.type].push(rec);
  });

  const resolved = [];

  // For each type, resolve conflicts
  Object.entries(byType).forEach(([type, recs]) => {
    if (recs.length === 1) {
      resolved.push(recs[0]);
      return;
    }

    // Multiple recommendations for same adjustment type
    // Priority: keep primary recommendations, merge secondary
    const primary = recs.filter(r => !r.isSecondary);
    const secondary = recs.filter(r => r.isSecondary);

    if (primary.length === 1) {
      resolved.push(primary[0]);
    } else if (primary.length > 1) {
      // Multiple primary recommendations for same type - average or pick highest priority
      const sorted = primary.sort((a, b) => a.priority - b.priority);
      
      // If directions conflict, keep higher priority only
      const directions = new Set(sorted.map(r => r.direction));
      if (directions.size > 1) {
        resolved.push(sorted[0]);
      } else {
        // Same direction - average the estimates
        const avgMm = Math.round(
          sorted.reduce((sum, r) => sum + (r.estimateMm || 0), 0) / sorted.length
        );
        resolved.push({
          ...sorted[0],
          estimateMm: avgMm,
          reason: sorted.map(r => r.reason).join(' & '),
        });
      }
    }

    // Add secondary recommendations if they don't conflict with primary
    secondary.forEach(sec => {
      const conflicts = resolved.some(
        r => r.type === sec.type && r.direction !== sec.direction
      );
      if (!conflicts) {
        resolved.push(sec);
      }
    });
  });

  return resolved;
}

/**
 * Group recommendations by adjustment category
 */
function groupRecommendations(recommendations) {
  const grouped = {};

  recommendations.forEach(rec => {
    if (!grouped[rec.type]) {
      grouped[rec.type] = [];
    }
    grouped[rec.type].push(rec);
  });

  // Sort each group by priority
  Object.keys(grouped).forEach(type => {
    grouped[type].sort((a, b) => a.priority - b.priority);
  });

  return grouped;
}

/**
 * Format a recommendation as a human-readable string
 */
export function formatRecommendation(rec) {
  if (rec.isGeneral) {
    return `${rec.action} — ${rec.detail}`;
  }

  let str = rec.action;
  if (rec.estimateMm) {
    str += ` ${rec.estimateMm}${rec.unit}`;
  }
  if (rec.reason) {
    str += ` (${rec.reason})`;
  }
  return str;
}
